import * as React from "react";

import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { cn } from "@/utils/style";

interface ConfirmOptions {
  description?: string;
  confirmText?: string;
  cancelText?: string;
  checkboxLabel?: string;
  onCheckedChange?: (checked: boolean) => void;
}

interface ConfirmState extends ConfirmOptions {
  open: boolean;
  title: string;
  checked: boolean;
  resolve: ((value: boolean) => void) | null;
}

type ConfirmContextType = {
  confirm: (title: string, options?: ConfirmOptions) => Promise<boolean>;
};

const ConfirmContext = React.createContext<ConfirmContextType | null>(null);

export function ConfirmDialogProvider({ children }: { children: React.ReactNode }) {
  const [state, setState] = React.useState<ConfirmState>({
    open: false,
    resolve: null,
    title: "",
    checked: false,
    description: undefined,
    confirmText: undefined,
    cancelText: undefined,
    checkboxLabel: undefined,
    onCheckedChange: undefined,
  });

  const confirm = React.useCallback(async (title: string, options?: ConfirmOptions): Promise<boolean> => {
    return new Promise<boolean>((resolve) => {
      setState({
        open: true,
        resolve,
        title,
        checked: false,
        description: options?.description,
        confirmText: options?.confirmText,
        cancelText: options?.cancelText,
        checkboxLabel: options?.checkboxLabel,
        onCheckedChange: options?.onCheckedChange,
      });
    });
  }, []);

  const handleConfirm = React.useCallback(() => {
    if (state.checkboxLabel) state.onCheckedChange?.(state.checked);
    if (state.resolve) state.resolve(true);

    setState((prev) => ({ ...prev, open: false, resolve: null }));
  }, [state.checked, state.checkboxLabel, state.onCheckedChange, state.resolve]);

  const handleCancel = React.useCallback(() => {
    if (state.resolve) state.resolve(false);

    setState((prev) => ({ ...prev, open: false, resolve: null }));
  }, [state.resolve]);

  return (
    <ConfirmContext.Provider value={{ confirm }}>
      {children}

      <AlertDialog open={state.open} onOpenChange={(open) => !open && handleCancel()}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{state.title}</AlertDialogTitle>
            <AlertDialogDescription className={cn(!state.description && "sr-only")}>
              {state.description}
            </AlertDialogDescription>
          </AlertDialogHeader>

          {state.checkboxLabel ? (
            <label className="flex items-center gap-2 text-sm text-muted-foreground">
              <input
                type="checkbox"
                className="size-4"
                checked={state.checked}
                onChange={(event) => setState((prev) => ({ ...prev, checked: event.target.checked }))}
              />
              <span>{state.checkboxLabel}</span>
            </label>
          ) : null}

          <AlertDialogFooter>
            <AlertDialogCancel onClick={handleCancel}>{state.cancelText ?? "Cancel"}</AlertDialogCancel>
            <AlertDialogAction onClick={handleConfirm}>{state.confirmText ?? "Confirm"}</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </ConfirmContext.Provider>
  );
}

export function useConfirm() {
  const context = React.useContext(ConfirmContext);

  if (!context) {
    throw new Error("useConfirm must be used within a <ConfirmDialogProvider />.");
  }

  return context.confirm;
}
